import React, { useEffect, useMemo } from 'react';
import { Navigate, Route, Routes, matchPath } from 'react-router-dom';

import MainLayout from 'pages/authenticated/layout/mainLayout/MainLayout';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';

import { userEndpoints } from 'hooks/endpoints';
import { useFetch } from 'hooks/useFetch';

import { useUserContext } from 'app-context/user/UserContext';
import { useSidebarData } from 'app-context/sidebar/SidebarDataProvider';

import {
  AGENT_ROUTES,
  BOT_SETTINGS_ROUTES,
  CONSOLE_SETTINGS_ROUTES,
  NONE_LAYOUT_BOT_ROUTES,
  ROUTES_MAP,
} from './data/Routes';

import Loader from 'component/atom/loader/Loader';
import MobileNotSupportedLayout from 'pages/authenticated/layout/mobileNotSupported/MobileNotSupportedLayout';
import Login from 'pages/authentication/login-new/LoginNew';

const loaderContainer = {
  width: '100%',
  height: '100vh',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};

const DASHBOARD_PATH =
  (ROUTES_MAP['/Dashboard'] && '/Dashboard') ||
  (ROUTES_MAP['/dashboard'] && '/dashboard') ||
  '/dashboard';

const MainRouter = () => {
  const token = localStorage.getItem('token');
  const isAuthenticated = !!token;

  const { sidebarData, isFetching } = useSidebarData();

  const { setUserData } = useUserContext();

  const { data: userData } = useFetch(['user'], userEndpoints.getAuthUser(), {
    enabled: !!token,
  });

  useEffect(() => {
    if (userData?.data) setUserData(userData.data);
  }, [userData, setUserData]);

  const isRestrictedUser = useMemo(() => {
    return userData?.data?.authUser?.roleId === 2;
  }, [userData]);

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path='/' element={<PublicRoute />}>
          <Route index element={<Login />} />
        </Route>
        <Route path='*' element={<Navigate to='/' replace />} />
      </Routes>
    );
  }

  if (!userData?.data) {
    return (
      <div style={loaderContainer}>
        <Loader />
      </div>
    );
  }
  if (isRestrictedUser) {
    const ChatConsole = ROUTES_MAP['/user/chat-console']?.element;
    const ChatConsoleWithId = ROUTES_MAP['/user/chat-console/:id']?.element;
    const ProfileEl = ROUTES_MAP['/user/profile']?.element;

    return (
      <Routes>
        <Route path='/' element={<PrivateRoute />}>
          <Route index element={<Navigate to='/user/chat-console' replace />} />

          <Route path='user/chat-console' element={ChatConsole} />
          <Route path='user/chat-console/:id' element={ChatConsoleWithId} />
          <Route path='user/profile' element={ProfileEl} />

          <Route
            path='*'
            element={<Navigate to='/user/chat-console' replace />}
          />
        </Route>
      </Routes>
    );
  }

  if (isFetching) {
    return (
      <div style={loaderContainer}>
        <Loader />
      </div>
    );
  }

  const groupedRoutes = { main: [], settings: [], console: [], none: [] };

  const STATIC_EXTRA_ROUTES = [
    '/knowledge-base/:id',
    '/user/:id',
    '/profile',
    '/docs',
    '/edit-user/:id',
    '/groups',
    '/new-group',
    '/new-user',
    '/edit-group/:id',
    '/bot/contact-profile',
    '/bot/script/:id',
  ];

  let validRoutes = [];
  if (Array.isArray(sidebarData?.data)) {
    const apiRoutes = sidebarData.data;
    const hasBots = apiRoutes.some((r) => r.routeTo === '/bots');
    const hasConsole = apiRoutes.some((r) => r.routeTo === '/chat-console');

    const allRoutes = [
      ...apiRoutes,
      ...(hasBots ? BOT_SETTINGS_ROUTES.map((p) => ({ routeTo: p })) : []),
      ...(hasBots ? NONE_LAYOUT_BOT_ROUTES.map((p) => ({ routeTo: p })) : []),
      ...(hasBots ? AGENT_ROUTES.map((p) => ({ routeTo: p })) : []),
      ...(hasConsole
        ? CONSOLE_SETTINGS_ROUTES.map((p) => ({ routeTo: p }))
        : []),
      ...STATIC_EXTRA_ROUTES.map((p) => ({ routeTo: p })),
    ];

    validRoutes = allRoutes.filter(({ routeTo }) => ROUTES_MAP[routeTo]);
  }

  validRoutes.forEach(({ routeTo }) => {
    const matchedKey = Object.keys(ROUTES_MAP).find((pattern) =>
      matchPath(pattern, routeTo),
    );

    const cfg = ROUTES_MAP[matchedKey || routeTo];
    if (!cfg) return;

    const layout = cfg.layout || 'main';
    groupedRoutes[layout].push(
      <Route key={routeTo} path={routeTo} element={cfg.element} />,
    );
  });

  const defaultNormalRoute =
    (ROUTES_MAP[DASHBOARD_PATH] && DASHBOARD_PATH) ||
    validRoutes?.[0]?.routeTo ||
    '/profile';

  return (
    <Routes>
      <Route path='/' element={<PrivateRoute />}>
        <Route index element={<Navigate to={defaultNormalRoute} replace />} />

        <Route element={<MobileNotSupportedLayout />}>
          <Route element={<MainLayout />}>
            {groupedRoutes.main}
            {groupedRoutes.settings}
            {groupedRoutes.console}
          </Route>
          {groupedRoutes.none}
        </Route>
      </Route>

      <Route path='*' element={<Navigate to={defaultNormalRoute} replace />} />
    </Routes>
  );
};

export default MainRouter;
